import {override} from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import ForumApplication from 'flarum/forum/ForumApplication';

const settingsPrefix = 'kilowhat-custom-paths.';

class ProfileRedirectPage extends Page {
    oninit(vnode: any) {
        super.oninit(vnode);

        m.route.set(app.route((app.current.data as any).routeName.replace('user.redirect.', ''), {
            username: m.route.param('username'),
        }));
    }

    view() {
        return LoadingIndicator.component();
    }
}

app.initializers.add('kilowhat-custom-paths', () => {
    // Use override because we need to call our code after boot() to get access to app.forum, but before m.route() is called in mount()
    override(ForumApplication.prototype, 'mount', function (this: ForumApplication, original: any, ...args: any[]) {
        // Default route code adapted from ForumApplication.mount
        // We need to run it before our own code otherwise we wouldn't be able to recognize a route from its old path
        const defaultRoute = app.forum.attribute('defaultRoute');
        let defaultAction = null;

        for (const i in app.routes) {
            if (!app.routes.hasOwnProperty(i)) {
                continue;
            }

            if (app.routes[i].path === defaultRoute) {
                defaultAction = i;
            }
        }

        const profilePrefix = app.forum.attribute(settingsPrefix + 'user') || 'u';
        const redirectUserFilterMapping: { [key: string]: string } = {};

        // Original routes defined in https://github.com/flarum/framework/blob/1.x/framework/core/js/src/forum/routes.ts
        if (app.forum.attribute(settingsPrefix + 'index') && app.routes.index) {
            app.routes.index.path = '/' + app.forum.attribute(settingsPrefix + 'index');
        }

        if (app.forum.attribute(settingsPrefix + 'discussion')) {
            if (app.routes.discussion) {
                app.routes.discussion.path = '/' + app.forum.attribute(settingsPrefix + 'discussion') + '/:id';
            }

            if (app.routes['discussion.near']) {
                app.routes['discussion.near'].path = '/' + app.forum.attribute(settingsPrefix + 'discussion') + '/:id/:near';
            }
        }

        if (app.forum.attribute(settingsPrefix + 'user')) {
            if (app.routes.user) {
                app.routes.user.path = '/' + profilePrefix + '/:username';
            }

            if (app.routes['user.posts']) {
                app.routes['user.posts'].path = '/' + profilePrefix + '/:username';
            }

            // Early support for Cimaise
            // Not implementing everything just now because the permalink system will receive an overhaul
            // https://github.com/kilowhat/flarum-ext-cimaise/blob/master/js/src/forum/addPages.ts
            if (app.routes.cimaiseUserPictures) {
                app.routes.cimaiseUserPictures.path = '/' + profilePrefix + '/:username/pictures';
            }
        }

        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'user-discussions')) {
            const filter = app.forum.attribute(settingsPrefix + 'user-discussions') || 'discussions';

            if (app.routes['user.discussions']) {
                app.routes['user.discussions'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.discussions = 'user.discussions';
            }
        }

        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'user-likes')) {
            const filter = app.forum.attribute(settingsPrefix + 'user-likes') || 'likes';

            // Original route defined in https://github.com/flarum/framework/blob/main/extensions/likes/js/src/forum/addLikesTabToUserProfile.tsx
            if (app.routes['user.likes']) {
                app.routes['user.likes'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.likes = 'user.likes';
            }
        }

        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'user-mentions')) {
            const filter = app.forum.attribute(settingsPrefix + 'user-mentions') || 'mentions';

            // Original route defined in https://github.com/flarum/mentions/blob/master/js/src/forum/index.js
            if (app.routes['user.mentions']) {
                app.routes['user.mentions'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.mentions = 'user.mentions';
            }
        }

        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'user-security')) {
            const filter = app.forum.attribute(settingsPrefix + 'user-security') || 'security';

            if (app.routes['user.security']) {
                app.routes['user.security'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.security = 'user.security';
            }
        }

        if (app.forum.attribute(settingsPrefix + 'settings') && app.routes.settings) {
            app.routes.settings.path = '/' + app.forum.attribute(settingsPrefix + 'settings');
        }

        if (app.forum.attribute(settingsPrefix + 'notifications') && app.routes.notifications) {
            app.routes.notifications.path = '/' + app.forum.attribute(settingsPrefix + 'notifications');
        }

        // Original routes defined in https://github.com/flarum/tags/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'tags') && app.routes.tags) {
            app.routes.tags.path = '/' + app.forum.attribute(settingsPrefix + 'tags');
        }

        if (app.forum.attribute(settingsPrefix + 'tag') && app.routes.tag) {
            app.routes.tag.path = '/' + app.forum.attribute(settingsPrefix + 'tag') + '/:tags';
        }

        // Original route defined in https://github.com/flarum/subscriptions/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'following') && app.routes.following) {
            app.routes.following.path = '/' + app.forum.attribute(settingsPrefix + 'following');
        }

        // Original route defined in https://github.com/flarum/flags/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'flags') && app.routes.flags) {
            app.routes.flags.path = '/' + app.forum.attribute(settingsPrefix + 'flags');
        }

        // Original routes defined in https://github.com/clarkwinkelmann/flarum-ext-formulaire/blob/master/js/src/forum/index.ts
        if (app.forum.attribute(settingsPrefix + 'formulaire-form') && app.routes.formulaireFormShow) {
            app.routes.formulaireFormShow.path = '/' + app.forum.attribute(settingsPrefix + 'formulaire-form') + '/:id';
        }
        if (app.forum.attribute(settingsPrefix + 'formulaire-submission') && app.routes.formulaireSubmission) {
            app.routes.formulaireSubmission.path = '/' + app.forum.attribute(settingsPrefix + 'formulaire-submission') + '/:id';
        }
        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'formulaire-profile')) {
            const filter = app.forum.attribute(settingsPrefix + 'formulaire-profile') || 'forms';

            if (app.routes.formulaireProfile) {
                app.routes.formulaireProfile.path = '/' + profilePrefix + '/:username/' + filter + '/:form';
                // Already redirected server-side since it doesn't match the PHP filter route
            }
        }

        // Original route defined in https://github.com/FriendsOfFlarum/user-directory/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'fof-user-directory') && app.routes.fof_user_directory) {
            app.routes.fof_user_directory.path = '/' + app.forum.attribute(settingsPrefix + 'fof-user-directory');
        }

        // Original route defined in https://github.com/FriendsOfFlarum/pages/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'fof-pages') && app.routes.page) {
            app.routes.page.path = '/' + app.forum.attribute(settingsPrefix + 'fof-pages') + '/:id';
        }

        // Original route defined in https://github.com/FriendsOfFlarum/gamification/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'fof-gamification') && app.routes.rankings) {
            app.routes.rankings.path = '/' + app.forum.attribute(settingsPrefix + 'fof-gamification');
        }
        // Original route defined in https://github.com/FriendsOfFlarum/gamification/blob/master/js/src/forum/addUpvoteTabToUserProfile.js
        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'fof-gamification-user-votes')) {
            const filter = app.forum.attribute(settingsPrefix + 'fof-gamification-user-votes') || 'votes';

            if (app.routes['user.votes']) {
                app.routes['user.votes'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.votes = 'user.votes';
            }
        }

        // Original routes defined in https://github.com/FriendsOfFlarum/byobu/blob/master/js/src/forum/pages/routes.js
        if (app.forum.attribute(settingsPrefix + 'fof-byobu') && app.routes.byobuPrivate) {
            app.routes.byobuPrivate.path = '/' + app.forum.attribute(settingsPrefix + 'fof-byobu');
        }
        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'fof-byobu-profile')) {
            const filter = app.forum.attribute(settingsPrefix + 'fof-byobu-profile') || 'private';

            if (app.routes.byobuUserPrivate) {
                app.routes.byobuUserPrivate.path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.private = 'byobuUserPrivate';
            }
        }

        // Original routes defined in https://github.com/FriendsOfFlarum/masquerade/blob/master/js/src/forum/addProfilePane.js
        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'fof-masquerade')) {
            const filter = app.forum.attribute(settingsPrefix + 'fof-masquerade') || 'masquerade';

            if (app.routes['fof-masquerade']) {
                app.routes['fof-masquerade'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.masquerade = 'fof-masquerade';
            }
        }

        // Original routes defined in https://github.com/FriendsOfFlarum/moderator-notes/blob/master/js/src/forum/addModeratorNotesPage.js
        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'fof-moderator-notes-user-notes')) {
            const filter = app.forum.attribute(settingsPrefix + 'fof-moderator-notes-user-notes') || 'notes';

            if (app.routes['user.notes']) {
                app.routes['user.notes'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.notes = 'user.notes';
            }
        }

        // Original route defined in https://github.com/askvortsov1/flarum-categories/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'askvortsov-categories') && app.routes.categories) {
            app.routes.categories.path = '/' + app.forum.attribute(settingsPrefix + 'askvortsov-categories');
        }

        // Original routes defined in https://github.com/v17development/flarum-blog/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'v17development-blog-overview') && app.routes.blog) {
            app.routes.blog.path = '/' + app.forum.attribute(settingsPrefix + 'v17development-blog-overview');
        }
        if (app.forum.attribute(settingsPrefix + 'v17development-blog-compose') && app.routes.blogComposer) {
            app.routes.blogComposer.path = '/' + app.forum.attribute(settingsPrefix + 'v17development-blog-compose');
        }
        if (app.forum.attribute(settingsPrefix + 'v17development-blog-category') && app.routes.blogCategory) {
            app.routes.blogCategory.path = '/' + app.forum.attribute(settingsPrefix + 'v17development-blog-category') + '/:slug';
        }
        if (app.forum.attribute(settingsPrefix + 'v17development-blog-post')) {
            if (app.routes.blogArticle) {
                app.routes.blogArticle.path = '/' + app.forum.attribute(settingsPrefix + 'v17development-blog-post') + '/:id';
            }
            if (app.routes['blogArticle.near']) {
                app.routes['blogArticle.near'].path = '/' + app.forum.attribute(settingsPrefix + 'v17development-blog-post') + '/:id/:near';
            }
        }

        // Original routes defined in https://github.com/v17development/flarum-user-badges/blob/main/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'v17development-user-badges-user-badges')) {
            const filter = app.forum.attribute(settingsPrefix + 'v17development-user-badges-user-badges') || 'badges';

            if (app.routes['user.badges']) {
                app.routes['user.badges'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping.badges = 'user.badges';
            }
        }
        if (app.forum.attribute(settingsPrefix + 'v17development-user-badges-overview') && app.routes.badges) {
            app.routes.badges.path = '/' + app.forum.attribute(settingsPrefix + 'v17development-user-badges-overview');
        }
        if (app.forum.attribute(settingsPrefix + 'v17development-user-badges-item') && app.routes['badges.item']) {
            app.routes['badges.item'].path = '/' + app.forum.attribute(settingsPrefix + 'v17development-user-badges-item') + '/:id';
        }

        // Original routes defined in https://github.com/flamarkt/core/blob/main/js/src/forum/routes.ts
        if (app.forum.attribute(settingsPrefix + 'flamarkt-cart') && app.routes['flamarkt.cart']) {
            app.routes['flamarkt.cart'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-cart');
        }
        if (app.forum.attribute(settingsPrefix + 'flamarkt-account') && app.routes['flamarkt.account']) {
            app.routes['flamarkt.account'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-account');
        }
        if (app.forum.attribute(settingsPrefix + 'flamarkt-orders') && app.routes['flamarkt.account.orders']) {
            app.routes['flamarkt.account.orders'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-orders');
        }
        if (app.forum.attribute(settingsPrefix + 'flamarkt-order') && app.routes['flamarkt.orders.show']) {
            app.routes['flamarkt.orders.show'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-order') + '/:id';
        }
        if (app.forum.attribute(settingsPrefix + 'flamarkt-products') && app.routes['flamarkt.products.index']) {
            app.routes['flamarkt.products.index'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-products');
        }
        if (app.forum.attribute(settingsPrefix + 'flamarkt-product') && app.routes['flamarkt.products.show']) {
            app.routes['flamarkt.products.show'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-product') + '/:id';
        }

        // Original route defined in https://github.com/flamarkt/balance/blob/main/js/src/forum/index.ts
        if (app.forum.attribute(settingsPrefix + 'flamarkt-balance') && app.routes['flamarkt.account.balance']) {
            app.routes['flamarkt.account.balance'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-balance');
        }

        // Original routes defined in https://github.com/flamarkt/categories/blob/main/js/src/forum/index.ts
        if (app.forum.attribute(settingsPrefix + 'flamarkt-categories') && app.routes['flamarkt.categories.index']) {
            app.routes['flamarkt.categories.index'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-categories');
        }
        if (app.forum.attribute(settingsPrefix + 'flamarkt-category') && app.routes['flamarkt.categories.show']) {
            app.routes['flamarkt.categories.show'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-category') + '/:id';
        }

        // Original route defined in https://github.com/flamarkt/identity/blob/main/js/src/forum/index.ts
        if (app.forum.attribute(settingsPrefix + 'flamarkt-identity') && app.routes['flamarkt.account.identity']) {
            app.routes['flamarkt.account.identity'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-identity');
        }

        // Original route defined in https://github.com/flamarkt/signup-page/blob/main/js/src/forum/index.ts
        if (app.forum.attribute(settingsPrefix + 'flamarkt-signup') && app.routes['flamarkt.signup']) {
            app.routes['flamarkt.signup'].path = '/' + app.forum.attribute(settingsPrefix + 'flamarkt-signup');
        }

        // Original route defined in https://github.com/clarkwinkelmann/flarum-ext-group-list/blob/master/js/src/forum/index.js
        if (app.forum.attribute(settingsPrefix + 'clarkwinkelmann-group-list') && app.routes['clarkwinkelmann-group-list']) {
            app.routes['clarkwinkelmann-group-list'].path = '/' + app.forum.attribute(settingsPrefix + 'clarkwinkelmann-group-list');
        }

        // Original route defined in https://github.com/clarkwinkelmann/flarum-ext-discussion-bookmarks/blob/master/js/src/forum/addPage.ts
        if (app.forum.attribute(settingsPrefix + 'clarkwinkelmann-discussion-bookmarks') && app.routes.discussionBookmarks) {
            app.routes.discussionBookmarks.path = '/' + app.forum.attribute(settingsPrefix + 'clarkwinkelmann-discussion-bookmarks');
        }

        // Original route defined in https://github.com/clarkwinkelmann/flarum-ext-post-bookmarks/blob/main/js/src/forum/addPage.js
        if (app.forum.attribute(settingsPrefix + 'clarkwinkelmann-post-bookmarks') && app.routes.postBookmarks) {
            app.routes.postBookmarks.path = '/' + app.forum.attribute(settingsPrefix + 'clarkwinkelmann-post-bookmarks');
        }

        // Original route defined in https://github.com/clarkwinkelmann/flarum-ext-discussion-lists/blob/main/js/src/forum/index.ts
        if (app.forum.attribute(settingsPrefix + 'clarkwinkelmann-discussion-list') && app.routes.discussionList) {
            app.routes.discussionList.path = '/' + app.forum.attribute(settingsPrefix + 'clarkwinkelmann-discussion-list') + '/:list';
        }
        // Original route defined in https://github.com/clarkwinkelmann/flarum-ext-discussion-lists/blob/main/js/src/forum/addProfilePage.ts
        if (app.forum.attribute(settingsPrefix + 'user') || app.forum.attribute(settingsPrefix + 'clarkwinkelmann-discussion-lists-profile')) {
            const filter = app.forum.attribute(settingsPrefix + 'clarkwinkelmann-discussion-lists-profile') || 'discussion-lists';

            if (app.routes['user.discussionLists']) {
                app.routes['user.discussionLists'].path = '/' + profilePrefix + '/:username/' + filter;
                redirectUserFilterMapping['discussion-lists'] = 'user.discussionLists';
            }
        }

        // Original routes defined in https://github.com/clarkwinkelmann/flarum-ext-composer-page/blob/main/js/src/forum/index.ts
        if (app.forum.attribute(settingsPrefix + 'clarkwinkelmann-composer-page') && app.routes.compose) {
            app.routes.compose.path = '/' + app.forum.attribute(settingsPrefix + 'clarkwinkelmann-composer-page');
        }
        if (app.forum.attribute(settingsPrefix + 'tag') || app.forum.attribute(settingsPrefix + 'clarkwinkelmann-composer-page-tag')) {
            const prefix = app.forum.attribute(settingsPrefix + 'tag') || 't';
            const filter = app.forum.attribute(settingsPrefix + 'clarkwinkelmann-composer-page-tag') || 'compose';

            if (app.routes.composeTag) {
                app.routes.composeTag.path = '/' + prefix + '/:slug/' + filter;
            }
        }

        const allRegisteredRoutes = Object.keys(app.routes).map(routeName => app.routes[routeName].path);

        Object.keys(redirectUserFilterMapping).forEach(filter => {
            const redirectFromPath = '/' + profilePrefix + '/:username/' + filter;
            const routeName = redirectUserFilterMapping[filter];

            // Check that the route hasn't been used by another rename before setting it up as a redirect
            // This feature will be applied even if "redirects" aren't enabled in the admin options
            // Because otherwise the page would redirect to the homepage and the apiPreload would have the wrong data type
            // This feature only covers the "filter" part of the URL. The prefix has already been redirected server-side
            // The mappings will exist in the object even if only the user prefix was customized, that's fine,
            // they simply will all get skipped due to the new routes existing already
            if (allRegisteredRoutes.indexOf(redirectFromPath) === -1) {
                // The original routeName will be extracted back out of our generated redirect routeName in the component
                app.routes['user.redirect.' + routeName] = {
                    path: redirectFromPath,
                    component: ProfileRedirectPage,
                };
            }
        });

        // Replace the default route path with the overridden path
        // So that the original app.mount can use the updated value
        app.forum.pushAttributes({
            defaultRoute: app.routes[defaultAction].path,
        });

        original.call(this, ...args);
    });
}, -100);
